import type { Asset, AssetKind } from '../shared/types';

/** Only immutable supplied WebPs belong here; never personal artwork or masks. */
export class PreparedStampWebps {
  private rows = new Map<string, Blob>();
  private bytes = 0;
  constructor(private maxBytes = 8 * 1024 * 1024, private maxEntries = 64) {}
  get(key: string) {
    const blob = this.rows.get(key);
    if (blob) { this.rows.delete(key); this.rows.set(key, blob); }
    return blob;
  }
  set(key: string, blob: Blob) {
    const previous = this.rows.get(key);
    if (previous) { this.bytes -= previous.size; this.rows.delete(key); }
    if (blob.size > this.maxBytes || this.maxEntries < 1) return;
    while (this.rows.size && (this.rows.size >= this.maxEntries || this.bytes + blob.size > this.maxBytes)) {
      const oldest = this.rows.keys().next().value!;
      this.bytes -= this.rows.get(oldest)!.size; this.rows.delete(oldest);
    }
    this.rows.set(key, blob); this.bytes += blob.size;
  }
}

/** Owned by one Catalog. Every hit must still exist in its current identity/board catalog. */
export class CatalogStampAssets {
  private rows = new Map<string, Pick<Asset, 'id' | 'url' | 'kind'>>();
  private context: string | undefined;
  constructor(private maxEntries = 96) {}
  setContext(context: string | undefined) {
    if (context !== this.context) { this.rows.clear(); this.context = context; }
  }
  private key(url: string, kind: AssetKind) { return `${kind}:${url}`; }
  find(url: string, kind: AssetKind, catalog: readonly Asset[]) {
    const key = this.key(url, kind), remembered = this.rows.get(key);
    if (!remembered) return undefined;
    const asset = catalog.find(value => value.id === remembered.id && value.url === remembered.url && value.kind === remembered.kind && value.kind === kind);
    this.rows.delete(key);
    if (asset) this.rows.set(key, remembered);
    return asset;
  }
  async prepare(url: string, kind: 'emoji' | 'sticker', catalog: () => readonly Asset[], loadSource: () => Promise<Blob>, upload: (blob: Blob) => Promise<Asset>, signal: AbortSignal) {
    const context = this.context;
    const check = () => {
      signal.throwIfAborted();
      if (context !== this.context) throw new DOMException('Stamp preparation changed. Choose again.', 'AbortError');
    };
    check();
    const retained = this.find(url, kind, catalog());
    if (retained) return retained;
    const blob = await loadSource(); check();
    const asset = await upload(blob); check();
    if (asset.kind !== kind) throw new Error('This stamp could not be prepared. Try again.');
    const key = this.key(url, kind); this.rows.delete(key);
    while (this.rows.size >= this.maxEntries && this.rows.size) this.rows.delete(this.rows.keys().next().value!);
    if (this.maxEntries > 0) this.rows.set(key, { id: asset.id, url: asset.url, kind: asset.kind });
    return asset;
  }
}
